import { useState, useEffect, useRef } from 'react'
import { ChevronDown, Printer, Truck, RefreshCw, Plus, X, Check, Search } from 'lucide-react'
import { downloadPdf } from '../../utils/pdfUtils'
import leiloesService from '../../services/leiloesService'
import mapasService from '../../services/mapasService'
import { motoristasService } from '../../services/motoristasService'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { useLabelCurral } from '../../hooks/useLabelCurral'
import { MotoristaRapidoModal } from '../cadastros/MotoristasList'

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:8001').replace(/\/$/, '')
const fmtDate = s => { if (!s) return ''; try { const [y, m, d] = s.split('T')[0].split('-'); return `${d}/${m}/${y}` } catch { return s } }
const fmtBRL = v => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

function Select({ label, value, onChange, children, disabled, className = '' }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {label && <label className="text-xs text-gray-500 font-medium">{label}</label>}
      <div className="relative">
        <select value={value} onChange={e => onChange(e.target.value)} disabled={disabled}
          className="w-full appearance-none border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400">
          {children}
        </select>
        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
      </div>
    </div>
  )
}

// ── Estilos V1 ──────────────────────────────────────────────────────────────
const S = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 13, color: '#000', background: '#fff', padding: '20px 28px 24px' },
  hdr: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '2px solid #1f2937', paddingBottom: 14, marginBottom: 18 },
  hdrLogo: { maxHeight: 80, maxWidth: 180, objectFit: 'contain' },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrT1: { fontSize: 20, fontWeight: 700 },
  hdrT2: { fontSize: 12, color: '#374151', marginTop: 3 },
  hdrT3: { fontSize: 13, fontWeight: 600, color: '#1f2937', marginTop: 2 },
  compBlock: { marginBottom: 14 },
  compLbl: { fontSize: 11, fontWeight: 700, fontStyle: 'italic', marginBottom: 4 },
  compNome: { fontSize: 15, fontWeight: 700 },
  compInfo: { fontSize: 12, color: '#374151', marginTop: 2 },
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 10 },
  th: { border: '1px solid #d1d5db', padding: '7px 8px', textAlign: 'left', fontWeight: 700, fontSize: 11, background: '#f3f4f6' },
  thC: { border: '1px solid #d1d5db', padding: '7px 8px', textAlign: 'center', fontWeight: 700, fontSize: 11, background: '#f3f4f6' },
  td: { border: '1px solid #e5e7eb', padding: '6px 8px', verticalAlign: 'middle', fontSize: 12 },
  tdC: { border: '1px solid #e5e7eb', padding: '6px 8px', textAlign: 'center', verticalAlign: 'middle', fontSize: 12 },
  totais: { textAlign: 'right', fontSize: 12, lineHeight: 1.8, marginTop: 12 },
  footer: { textAlign: 'left', fontSize: 11, color: '#6b7280', marginTop: 20, paddingTop: 10, borderTop: '1px solid #e5e7eb' },
}

// ── Preview V1 ───────────────────────────────────────────────────────────────
const V1_ALL_COLS = [
  { id: 'lote',        pct: 5,  label: 'Lote',        left: true },
  { id: 'vendedor',    pct: 22, label: 'Vendedor',     left: true },
  { id: 'curral',      pct: 8,  label: 'Curral',       left: false },
  { id: 'situacao',    pct: 9,  label: 'Situação',     left: false },
  { id: 'qtd',         pct: 6,  label: 'Qtd',          left: false },
  { id: 'sexo',        pct: 8,  label: 'Sexo',         left: false },
  { id: 'idade',       pct: 8,  label: 'Idade',        left: false },
  { id: 'crias_femea', pct: 10, label: 'Crias Fêmea',  left: false },
  { id: 'crias_macho', pct: 10, label: 'Crias Macho',  left: false },
]

function PreviewV1({ dados, leilaoAtual, organizadorAtivo, logoOrgSrc, placa, motoristaNome, vFrete, totalQtd, totalCriasFemea, totalCriasMacho, ordemEmbarque, colunas }) {
  const labelCurral = useLabelCurral()
  const colIds = colunas || V1_ALL_COLS.map(c => c.id)
  const activeCols = V1_ALL_COLS.filter(c => colIds.includes(c.id)).map(c => c.id === 'curral' ? { ...c, label: labelCurral } : c)
  const totalPct = activeCols.reduce((s, c) => s + c.pct, 0)


  return (
    <div style={S.page}>
      <div style={S.hdr}>
        <div style={{ minWidth: 140 }}>{logoOrgSrc ? <img src={logoOrgSrc} alt="logo" style={S.hdrLogo} /> : <span style={{ fontWeight: 700 }}>{organizadorAtivo?.name || ''}</span>}</div>
        <div style={S.hdrCenter}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            <div style={S.hdrT1}>LIBERAÇÃO</div>
            {ordemEmbarque && (
              <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: '50%', background: '#dc2626', color: '#fff', fontWeight: 800, fontSize: 16, lineHeight: 1 }}>
                {ordemEmbarque}º
              </div>
            )}
          </div>
          <div style={S.hdrT2}>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</div>
          <div style={S.hdrT3}>{leilaoAtual?.nome} — {fmtDate(leilaoAtual?.data_leilao)}</div>
        </div>
        <div style={{ minWidth: 140, textAlign: 'right' }}><img src="/assets/rematei.png" alt="Rematei" style={S.hdrLogo} /></div>
      </div>

      <div style={{ ...S.compBlock, display: 'flex', gap: 24 }}>
        <div style={{ flex: '0 0 55%' }}>
          <div style={S.compLbl}>Dados Comprador</div>
          <div style={S.compNome}>{dados.comprador.nome} {dados.comprador.cpf_cnpj}</div>
          {dados.comprador.cidade && <div style={S.compInfo}>{dados.comprador.cidade}{dados.comprador.uf ? ` - ${dados.comprador.uf}` : ''}</div>}
          {dados.comprador.telefone && <div style={S.compInfo}>{dados.comprador.telefone}</div>}
        </div>
        <div style={{ flex: '0 0 45%' }}>
          {dados.comprador.fazenda && (
            <>
              <div style={S.compLbl}>Fazenda</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{dados.comprador.fazenda}</div>
              {dados.comprador.fazenda_ie && <div style={S.compInfo}>IE: {dados.comprador.fazenda_ie}</div>}
              {dados.comprador.fazenda_cidade && <div style={S.compInfo}>{dados.comprador.fazenda_cidade}{dados.comprador.fazenda_uf ? ` - ${dados.comprador.fazenda_uf}` : ''}</div>}
            </>
          )}
        </div>
      </div>

      <table style={S.tbl}>
        <thead><tr>
          {activeCols.map(col => (
            <th key={col.id} style={{ ...(col.left ? S.th : S.thC), width: `${(col.pct / totalPct * 100).toFixed(1)}%` }}>{col.label}</th>
          ))}
        </tr></thead>
        <tbody>
          {dados.lotes.map((l, i) => (
            <tr key={i}>
              {activeCols.map(col => {
                if (col.id === 'lote') return <td key={col.id} style={S.td}><strong>{l.numero_lote}</strong></td>
                if (col.id === 'vendedor') return <td key={col.id} style={S.td}><div style={{ fontWeight: 600 }}>{l.vendedor}</div><div style={{ fontSize: 11, fontWeight: 700 }}>{l.descricao}</div></td>
                if (col.id === 'curral') return <td key={col.id} style={S.tdC}>{l.curral || '—'}</td>
                if (col.id === 'situacao') return <td key={col.id} style={S.tdC}>{l.situacao}</td>
                if (col.id === 'qtd') return <td key={col.id} style={S.tdC}>{l.qtd}</td>
                if (col.id === 'sexo') return <td key={col.id} style={S.tdC}>{l.sexo || '—'}</td>
                if (col.id === 'idade') return <td key={col.id} style={S.tdC}>{l.idade || '—'}</td>
                if (col.id === 'crias_femea') return <td key={col.id} style={S.tdC}>{l.crias_femea || 0}</td>
                if (col.id === 'crias_macho') return <td key={col.id} style={S.tdC}>{l.crias_macho || 0}</td>
                return <td key={col.id} style={S.tdC}>—</td>
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {(() => {
        const machos = dados.lotes.filter(l => (l.sexo || '').toUpperCase() === 'MACHO').reduce((s, l) => s + l.qtd, 0)
        const femeas = dados.lotes.filter(l => ['FEMEA', 'FÊMEA'].includes((l.sexo || '').toUpperCase())).reduce((s, l) => s + l.qtd, 0)
        const mistos = totalQtd - machos - femeas
        return (
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderTop: '2px solid #1f2937', borderBottom: '1px solid #d1d5db', marginTop: 12, fontSize: 12, fontWeight: 700 }}>
            <span>TOTAL DE MACHOS: {machos}</span>
            <span>TOTAL DE FÊMEAS: {femeas}</span>
            <span>TOTAL DE MISTOS: {mistos}</span>
            <span>TOTAL DE ANIMAIS: {totalQtd}</span>
          </div>
        )
      })()}

      <div style={S.totais}>
        <div>Qtd. Lotes: <strong>{dados.lotes.length}</strong></div>
        {colIds.includes('crias_femea') && <div>Crias Fêmea: <strong>{totalCriasFemea}</strong></div>}
        {colIds.includes('crias_macho') && <div>Crias Macho: <strong>{totalCriasMacho}</strong></div>}
        {(colIds.includes('crias_femea') || colIds.includes('crias_macho')) && (
          <div>Qtd. Animais + Crias: <strong>{totalQtd + totalCriasFemea + totalCriasMacho}</strong></div>
        )}
      </div>

      <div style={{ marginTop: 20, fontSize: 12, lineHeight: 1.8 }}>
        <div><strong>{organizadorAtivo?.name || organizadorAtivo?.nome || ''}</strong>, autoriza o embarque de</div>
        <div><strong>{totalQtd} animal(is)</strong> dos lote(s) acima discriminados.</div>
        <div style={{ marginTop: 12, color: '#6b7280', fontSize: 11 }}>
          {organizadorAtivo?.cidade || ''}{organizadorAtivo?.uf ? ` - ${organizadorAtivo.uf}` : ''}, {new Date().toLocaleDateString('pt-BR')}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
        <div style={{ textAlign: 'center', minWidth: 250 }}>
          <div style={{ borderTop: '1px solid #1f2937', paddingTop: 6, fontSize: 12, fontWeight: 700 }}>
            {organizadorAtivo?.name || organizadorAtivo?.nome || ''}
          </div>
          <div style={{ fontSize: 10, color: '#6b7280' }}>Assinatura / Carimbo</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, marginTop: 16, paddingTop: 12, borderTop: '1px solid #d1d5db' }}>
        <div style={{ flex: '0 0 30%', border: '1px solid #d1d5db', padding: '8px 12px', fontSize: 12 }}>
          PLACA DO VEÍCULO: <strong>{placa || '___________'}</strong>
        </div>
        <div style={{ flex: '0 0 45%', border: '1px solid #d1d5db', borderLeft: 'none', padding: '8px 12px', fontSize: 12 }}>
          MOTORISTA: <strong>{motoristaNome || '________________________________'}</strong>
        </div>
        <div style={{ flex: '0 0 25%', border: '1px solid #d1d5db', borderLeft: 'none', padding: '8px 12px', fontSize: 12 }}>
          FRETE: <strong>{vFrete > 0 ? fmtBRL(vFrete) : '___________'}</strong>
        </div>
      </div>

      <div style={S.footer}>www.rematei.com.br</div>
    </div>
  )
}

// ── Preview V2 ───────────────────────────────────────────────────────────────
const V2 = {
  page: { fontFamily: 'Arial, Helvetica, sans-serif', fontSize: 12, color: '#000', background: '#fff', padding: '24px 32px 28px' },
  logo: { maxHeight: 68, maxWidth: 150, objectFit: 'contain' },
  // Header
  hdrWrap: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 },
  hdrCenter: { flex: 1, textAlign: 'center', padding: '0 16px' },
  hdrOrgName: { fontSize: 13, fontWeight: 700, textTransform: 'uppercase' },
  hdrLeilao: { fontSize: 11, fontWeight: 600, marginTop: 2 },
  hdrSub: { fontSize: 10, color: '#374151', marginTop: 1 },
  bigTitle: { fontSize: 20, fontWeight: 700, letterSpacing: 8, textAlign: 'center', marginTop: 6, marginBottom: 12, textTransform: 'uppercase' },
  // Buyer box
  buyerBox: { border: '1px solid #000', marginBottom: 10 },
  buyerHeader: { background: '#d1d5db', padding: '4px 10px', fontWeight: 700, fontSize: 12, textAlign: 'center', textTransform: 'uppercase', borderBottom: '1px solid #000' },
  buyerLabel: { fontWeight: 700, textTransform: 'uppercase' },
  // Table
  tbl: { width: '100%', borderCollapse: 'collapse', fontSize: 10, marginBottom: 8 },
  th: { border: '1px solid #000', padding: '5px 6px', textAlign: 'center', fontWeight: 700, fontSize: 10, background: '#f3f4f6' },
  td: { border: '1px solid #9ca3af', padding: '4px 6px', textAlign: 'center', verticalAlign: 'middle', fontSize: 10 },
  tdLeft: { border: '1px solid #9ca3af', padding: '4px 6px', textAlign: 'left', verticalAlign: 'middle', fontSize: 10 },
  // Totais
  totaisLine: { fontSize: 11, fontWeight: 500, margin: '8px 0 4px', display: 'flex', gap: 32 },
  // Auth
  authText: { fontSize: 11, textAlign: 'center', marginTop: 14, lineHeight: 1.8 },
  sigLine: { borderTop: '1px solid #000', width: 280, margin: '0 auto', marginTop: 36, paddingTop: 6, textAlign: 'center', fontSize: 11, fontWeight: 700 },
  // Control box
  controlBox: { border: '2px solid #000', marginTop: 18 },
  controlHeader: { borderBottom: '2px solid #000', padding: '5px 12px', fontWeight: 700, fontSize: 10, textAlign: 'center', letterSpacing: 3, textTransform: 'uppercase' },
  controlBody: { padding: '16px 20px', display: 'flex', justifyContent: 'space-between', gap: 20, minHeight: 140 },
  controlLeft: { display: 'flex', flexDirection: 'column', gap: 16, fontSize: 14, fontWeight: 700 },
  controlRight: { display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignItems: 'flex-end', flex: 1 },
  sigSmall: { borderTop: '1px solid #000', width: 240, paddingTop: 4, fontSize: 10, fontWeight: 700, textAlign: 'center' },
}

const V2_ALL_COLS = [
  { id: 'lote',        pct: 7,  label: 'LOTE' },
  { id: 'situacao',    pct: 9,  label: 'SITUAÇÃO' },
  { id: 'qtd',         pct: 7,  label: 'QTD.' },
  { id: 'sexo',        pct: 7,  label: 'SEXO' },
  { id: 'idade',       pct: 9,  label: 'IDADE' },
  { id: 'categoria',   pct: 16, label: 'CATEGORIA' },
  { id: 'brincos_gta', pct: 28, label: 'BRINCOS / GTA' },
  { id: 'crias',       pct: 7,  label: 'CRIAS' },
]

function PreviewV2({ dados, leilaoAtual, organizadorAtivo, logoOrgSrc, placa, totalQtd, ordemEmbarque, colunas }) {
  const colIds2 = colunas || V2_ALL_COLS.map(c => c.id)
  const activeCols2 = V2_ALL_COLS.filter(c => colIds2.includes(c.id))
  const totalPct2 = activeCols2.reduce((s, c) => s + c.pct, 0)

  const orgName = organizadorAtivo?.name || organizadorAtivo?.nome || ''
  const orgCidade = organizadorAtivo?.cidade || ''
  const orgUf = organizadorAtivo?.uf || ''
  const dataHoje = new Date().toLocaleDateString('pt-BR')

  const machos = dados.lotes.filter(l => (l.sexo || '').toUpperCase() === 'MACHO').reduce((s, l) => s + l.qtd, 0)
  const femeas = dados.lotes.filter(l => ['FEMEA', 'FÊMEA'].includes((l.sexo || '').toUpperCase())).reduce((s, l) => s + l.qtd, 0)
  const outros = totalQtd - machos - femeas

  const comprador = dados.comprador

  return (
    <div style={V2.page}>
      {/* ─── Cabeçalho ─── */}
      <div style={V2.hdrWrap}>
        <div style={{ minWidth: 130 }}>
          {logoOrgSrc
            ? <img src={logoOrgSrc} alt="logo" style={V2.logo} />
            : <span style={{ fontWeight: 700, fontSize: 14 }}>{orgName}</span>}
        </div>
        <div style={V2.hdrCenter}>
          <div style={V2.hdrOrgName}>{orgName}</div>
          <div style={V2.hdrLeilao}>{leilaoAtual?.nome}</div>
          <div style={V2.hdrSub}>{orgName} - {fmtDate(leilaoAtual?.data_leilao)} - {orgCidade}{orgUf ? ` - ${orgUf}` : ''}</div>
        </div>
        <div style={{ minWidth: 130, textAlign: 'right' }}>
          <img src="/assets/rematei.png" alt="Rematei" style={V2.logo} />
        </div>
      </div>

      <div style={{ ...V2.bigTitle, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
        <span>L I B E R A Ç Ã O &nbsp; D E &nbsp; E M B A R Q U E</span>
        {ordemEmbarque && (
          <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40, borderRadius: '50%', background: '#dc2626', color: '#fff', fontWeight: 800, fontSize: 18, lineHeight: 1, flexShrink: 0 }}>
            {ordemEmbarque}º
          </div>
        )}
      </div>

      {/* ─── Bloco Comprador ─── */}
      <div style={V2.buyerBox}>
        <div style={V2.buyerHeader}>{comprador.nome}</div>
        <div style={{ padding: '6px 10px', fontSize: 11, lineHeight: 1.7 }}>
          <div><span style={V2.buyerLabel}>Comprador:</span> {comprador.nome}<span style={{ float: 'right' }}><span style={V2.buyerLabel}>CPF/CNPJ:</span> {comprador.cpf_cnpj || '—'}</span></div>
          <div><span style={V2.buyerLabel}>TITULAR:</span> {comprador.nome}<span style={{ float: 'right' }}><span style={V2.buyerLabel}>TELEFONE:</span> {comprador.telefone || '—'}</span></div>
          <div><span style={V2.buyerLabel}>FAZENDA:</span> {comprador.fazenda || '—'}</div>
          <div><span style={V2.buyerLabel}>ROTA:</span> - <span style={V2.buyerLabel}>MUN.:</span> {comprador.fazenda_cidade || comprador.cidade || '—'} - <span style={V2.buyerLabel}>UF:</span>{comprador.fazenda_uf || comprador.uf || '—'} - <span style={V2.buyerLabel}>CEP:</span>{comprador.cep || '—'}</div>
          <div><span style={V2.buyerLabel}>SANITÁRIO:</span> - <span style={V2.buyerLabel}>INSCRIÇÃO:</span> {comprador.fazenda_ie || ''}</div>
          <div><span style={V2.buyerLabel}>OBS.:</span></div>
        </div>
      </div>

      {/* ─── Tabela de Lotes ─── */}
      <table style={V2.tbl}>
        <thead>
          <tr>
            {activeCols2.map(col => (
              <th key={col.id} style={{ ...V2.th, width: `${(col.pct / totalPct2 * 100).toFixed(1)}%` }}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dados.lotes.map((l, i) => (
            <tr key={i}>
              {activeCols2.map(col => {
                const crias = (l.crias_femea || 0) + (l.crias_macho || 0)
                if (col.id === 'lote') return <td key={col.id} style={V2.td}><strong>{l.numero_lote}</strong></td>
                if (col.id === 'situacao') return <td key={col.id} style={V2.td}>{l.situacao || 'COMPRA'}</td>
                if (col.id === 'qtd') return <td key={col.id} style={V2.td}>{l.qtd}</td>
                if (col.id === 'sexo') return <td key={col.id} style={V2.td}>{l.sexo ? l.sexo.charAt(0).toUpperCase() : '—'}</td>
                if (col.id === 'idade') return <td key={col.id} style={V2.td}>{l.idade || '—'}</td>
                if (col.id === 'categoria') return <td key={col.id} style={V2.td}>{l.tipo || ''}</td>
                if (col.id === 'brincos_gta') return <td key={col.id} style={V2.tdLeft}>{l.observacao || ''}</td>
                if (col.id === 'crias') return <td key={col.id} style={V2.td}>{crias || ''}</td>
                return <td key={col.id} style={V2.td}>—</td>
              })}
            </tr>
          ))}
        </tbody>
      </table>

      {/* ─── Totais ─── */}
      <div style={V2.totaisLine}>
        <span>Total de Machos : {machos}</span>
        <span>Total de Fêmeas : {femeas}</span>
        <span>Outros : {outros}</span>
      </div>

      {/* ─── Texto de Autorização ─── */}
      <div style={V2.authText}>
        <div style={{ textIndent: 40, textAlign: 'justify' }}>
          {orgName}, AUTORIZA O EMBARQUE DE <strong>{totalQtd} ANIMAL(IS)</strong>, DOS LOTES ACIMA DISCRIMINADOS.
        </div>
        <div style={{ marginTop: 20 }}>ATENCIOSAMENTE,</div>
        <div style={{ marginTop: 28, textAlign: 'center' }}>
          <div style={{ fontSize: 11, color: '#374151' }}>
            {orgCidade ? `${orgCidade}${orgUf ? ` - ${orgUf}` : ''}, ${dataHoje}.` : dataHoje + '.'}
          </div>
        </div>
        <div style={V2.sigLine}>{orgName}</div>
      </div>

      {/* ─── Controle Manual para o Manejo - Embarque ─── */}
      <div style={V2.controlBox}>
        <div style={V2.controlHeader}>C O N T R O L E &nbsp;&nbsp; M A N U A L &nbsp;&nbsp; P A R A &nbsp;&nbsp; O &nbsp;&nbsp; M A N E J O &nbsp;&nbsp; - &nbsp;&nbsp; E M B A R Q U E</div>
        <div style={V2.controlBody}>
          <div style={V2.controlLeft}>
            <div>
              <strong>DATA:</strong>&nbsp;&nbsp;&nbsp;
              <span style={{ borderBottom: '2px solid #000', display: 'inline-block', minWidth: 30, textAlign: 'center' }}>&nbsp;&nbsp;&nbsp;</span>
              <span style={{ margin: '0 4px' }}>/</span>
              <span style={{ borderBottom: '2px solid #000', display: 'inline-block', minWidth: 30, textAlign: 'center' }}>&nbsp;&nbsp;&nbsp;</span>
              <span style={{ margin: '0 4px' }}>/</span>
              <span style={{ borderBottom: '2px solid #000', display: 'inline-block', minWidth: 50, textAlign: 'center' }}>&nbsp;&nbsp;&nbsp;&nbsp;</span>
            </div>
            <div>
              <strong>HORÁRIO:</strong>&nbsp;&nbsp;&nbsp;
              <span style={{ borderBottom: '2px solid #000', display: 'inline-block', minWidth: 34, textAlign: 'center' }}>&nbsp;&nbsp;&nbsp;</span>
              <span style={{ margin: '0 4px' }}>:</span>
              <span style={{ borderBottom: '2px solid #000', display: 'inline-block', minWidth: 34, textAlign: 'center' }}>&nbsp;&nbsp;&nbsp;</span>
            </div>
            <div style={{ marginTop: 12 }}>
              <strong>PLACA:</strong>&nbsp;&nbsp;&nbsp;
              <span style={{ borderBottom: '2px solid #000', display: 'inline-block', minWidth: 140, fontFamily: 'monospace', fontSize: 14 }}>
                {placa ? <strong>{placa}</strong> : <span>&nbsp;</span>}
              </span>
            </div>
          </div>
          <div style={V2.controlRight}>
            <div style={V2.sigSmall}>RESPONSÁVEL PELO EMBARQUE</div>
            <div style={{ ...V2.sigSmall, marginTop: 36 }}>MOTORISTA RESPONSÁVEL</div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function LiberacaoCurral() {
  const { organizadorAtivo } = useOrganizador()
  const orgId = organizadorAtivo?.id

  const [leiloes, setLeiloes] = useState([])
  const [compradores, setCompradores] = useState([])
  const [leilaoId, setLeilaoId] = useState('')
  const [tipo, setTipo] = useState('compras')
  const [compradorId, setCompradorId] = useState('')
  const [dados, setDados] = useState(null)

  const [placa, setPlaca] = useState('')
  const [motoristaNome, setMotoristaNome] = useState('')
  const [motoristaId, setMotoristaId] = useState(null)
  const [valorFrete, setValorFrete] = useState('')
  const [ordemEmbarque, setOrdemEmbarque] = useState('')

  const [sugestoes, setSugestoes] = useState([])
  const [buscandoMotorista, setBuscandoMotorista] = useState(false)
  const [dropdownAberto, setDropdownAberto] = useState(false)
  const autoRef = useRef(null)
  const debRef = useRef(null)

  const [adicionado, setAdicionado] = useState(false)
  const [salvando, setSalvando] = useState(false)
  const [showMotoristaModal, setShowMotoristaModal] = useState(false)

  const [versao, setVersao] = useState('1')
  const [colunasV1, setColunasV1] = useState(['lote', 'vendedor', 'curral', 'situacao', 'qtd', 'sexo', 'idade', 'crias_femea', 'crias_macho'])
  const [colunasV2, setColunasV2] = useState(['lote', 'situacao', 'qtd', 'sexo', 'idade', 'categoria', 'brincos_gta', 'crias'])
  const [loadingConfig, setLoadingConfig] = useState(true)

  const [loading, setLoading] = useState(false)
  const [loadingL, setLoadingL] = useState(true)
  const [loadingC, setLoadingC] = useState(false)
  const [loadingPdf, setLoadingPdf] = useState(false)
  const [pdfUrl, setPdfUrl] = useState(null)
  const [showPdf, setShowPdf] = useState(false)
  const iframeRef = useRef(null)

  // Carrega versão padrão da config
  useEffect(() => {
    if (!orgId) return
    setLoadingConfig(true)
    mapasService.getConfig(orgId)
      .then(data => {
        if (data?.liberacao_curral_versao_padrao) setVersao(data.liberacao_curral_versao_padrao)
        if (data?.liberacao_curral_v1_colunas_ativas) setColunasV1(data.liberacao_curral_v1_colunas_ativas)
        if (data?.liberacao_curral_v2_colunas_ativas) setColunasV2(data.liberacao_curral_v2_colunas_ativas)
      })
      .catch(() => {})
      .finally(() => setLoadingConfig(false))
  }, [orgId])

  useEffect(() => {
    const h = e => { if (autoRef.current && !autoRef.current.contains(e.target)) setDropdownAberto(false) }
    document.addEventListener('mousedown', h)
    return () => document.removeEventListener('mousedown', h)
  }, [])

  useEffect(() => {
    if (!orgId) return
    setLoadingL(true)
    leiloesService.getByOrganizador(orgId)
      .then(data => {
        const sorted = [...(data || [])].sort((a, b) => new Date(b.data_leilao) - new Date(a.data_leilao))
        setLeiloes(sorted)
        if (sorted.length) setLeilaoId(String((sorted.find(l => l.status === 'on') || sorted[0]).id))
      })
      .finally(() => setLoadingL(false))
  }, [orgId])

  useEffect(() => {
    if (!orgId || !leilaoId) { setCompradores([]); return }
    setLoadingC(true)
    setCompradorId('')
    setDados(null)
    mapasService.getCompradores(orgId, leilaoId, { tipo })
      .then(list => { setCompradores(list); setCompradorId(list.length ? String(list[0].id) : '') })
      .catch(() => setCompradores([]))
      .finally(() => setLoadingC(false))
  }, [orgId, leilaoId, tipo])

  useEffect(() => {
    if (!orgId || !leilaoId || !compradorId || compradorId === 'todos') { setDados(null); return }
    setLoading(true)
    mapasService.getLiberacaoCurralDados(orgId, leilaoId, compradorId, tipo)
      .then(setDados)
      .catch(() => setDados(null))
      .finally(() => setLoading(false))
  }, [orgId, leilaoId, compradorId, tipo])

  useEffect(() => {
    if (!orgId || !leilaoId || !compradorId || compradorId === 'todos') return
    mapasService.getLiberacaoInfo(orgId, leilaoId, compradorId)
      .then(info => {
        if (info?.motorista_nome || info?.placa) {
          setMotoristaNome(info.motorista_nome || '')
          setPlaca(info.placa || '')
          setMotoristaId(info.motorista_id || null)
          setValorFrete(info.valor_frete > 0 ? String(info.valor_frete) : '')
          setOrdemEmbarque(info.ordem_embarque != null ? String(info.ordem_embarque) : '')
          setAdicionado(true)
        } else {
          setMotoristaNome(''); setPlaca(''); setMotoristaId(null); setValorFrete(''); setOrdemEmbarque(''); setAdicionado(false)
        }
      })
      .catch(() => {})
  }, [orgId, leilaoId, compradorId])

  const handleMotoristaChange = val => {
    setMotoristaNome(val); setMotoristaId(null); setAdicionado(false)
    clearTimeout(debRef.current)
    if (!val.trim()) { setSugestoes([]); setBuscandoMotorista(false); return }
    setBuscandoMotorista(true)
    debRef.current = setTimeout(async () => {
      try {
        const res = await motoristasService.buscar(orgId, val)
        setSugestoes(res.data || [])
        setDropdownAberto(true)
      } catch { setSugestoes([]) }
      finally { setBuscandoMotorista(false) }
    }, 350)
  }

  const handlePlacaChange = val => { setPlaca(val.toUpperCase()); setAdicionado(false) }

  const handleSelecionarSugestao = s => {
    setMotoristaNome(s.motorista_nome); setPlaca(s.placa || ''); setMotoristaId(s.motorista_id)
    setSugestoes([]); setDropdownAberto(false); setAdicionado(false)
  }

  const handleMotoristaCriado = m => {
    setMotoristaNome(m.nome); setMotoristaId(m.id); setSugestoes([]); setAdicionado(false)
  }

  const limparMotorista = () => {
    setMotoristaNome(''); setPlaca(''); setMotoristaId(null); setValorFrete(''); setOrdemEmbarque(''); setAdicionado(false); setSugestoes([])
  }

  const handleAdicionar = async () => {
    if (!motoristaNome.trim() && !placa.trim()) return
    setSalvando(true)
    try {
      await mapasService.salvarLiberacaoInfo(orgId, leilaoId, {
        compradorId, motoristaId,
        motoristaNome: motoristaNome.trim(), placa: placa.trim(),
        valorFrete: parseFloat(String(valorFrete).replace(',', '.')) || 0,
        ordemEmbarque: ordemEmbarque ? parseInt(ordemEmbarque) : null,
      })
      setAdicionado(true)
    } catch (e) { console.error('Erro ao salvar info da liberação:', e) }
    finally { setSalvando(false) }
  }

  const leilaoAtual = leiloes.find(l => String(l.id) === leilaoId)
  const logoOrgSrc = organizadorAtivo?.logo_url
    ? (organizadorAtivo.logo_url.startsWith('http') ? organizadorAtivo.logo_url
        : `${API_BASE}${organizadorAtivo.logo_url.startsWith('/') ? '' : '/'}${organizadorAtivo.logo_url}`)
    : null

  const vFrete = parseFloat(String(valorFrete).replace(',', '.')) || 0

  const handleImprimir = async () => {
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getLiberacaoCurralPdf(orgId, leilaoId, compradorId, placa, motoristaNome, vFrete, versao, tipo)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url); setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }

  const closePdf = () => { setShowPdf(false); if (pdfUrl) { URL.revokeObjectURL(pdfUrl); setPdfUrl(null) } }
  const printPdf = () => { if (iframeRef.current?.contentWindow) { iframeRef.current.contentWindow.focus(); iframeRef.current.contentWindow.print() } }

  const handleImprimirGeral = async () => {
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getLiberacaoCurralGeralPdf(orgId, leilaoId, tipo)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url); setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }

  const handleOrdemEmbarque = async () => {
    setLoadingPdf(true)
    try {
      const blob = await mapasService.getOrdemEmbarquePdf(orgId, leilaoId)
      if (pdfUrl) URL.revokeObjectURL(pdfUrl)
      const url = URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }))
      setPdfUrl(url); setShowPdf(true)
    } catch (err) { alert('Erro: ' + (err.response?.statusText || err.message)) }
    finally { setLoadingPdf(false) }
  }

  if (!orgId) return <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">Selecione um organizador.</div>

  const totalQtd = dados?.lotes?.reduce((s, l) => s + l.qtd, 0) || 0
  const totalCriasFemea = dados?.lotes?.reduce((s, l) => s + (l.crias_femea || 0), 0) || 0
  const totalCriasMacho = dados?.lotes?.reduce((s, l) => s + (l.crias_macho || 0), 0) || 0
  const podeAdicionar = (motoristaNome.trim() || placa.trim()) && !adicionado

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Truck className="w-5 h-5 text-green-600" />
        <h1 className="text-lg font-semibold text-gray-800">Liberação Curral</h1>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 space-y-3">
        {/* Linha 1: Leilão + Tipo + Comprador + Versão + Imprimir */}
        <div className="flex flex-wrap gap-3">
          <Select label="Leilão" value={leilaoId} onChange={v => { setLeilaoId(v); setDados(null); setAdicionado(false) }} disabled={loadingL} className="flex-[2] min-w-[180px]">
            {loadingL ? <option>Carregando...</option> : leiloes.map(l => <option key={l.id} value={String(l.id)}>{l.nome}</option>)}
          </Select>
          <Select label="Tipo" value={tipo} onChange={v => setTipo(v)} disabled={!leilaoId} className="flex-[1.5] min-w-[150px]">
            <option value="compras">COMPRAS</option>
            <option value="compras_defesas">COMPRAS E DEFESAS</option>
            <option value="defesas">DEFESAS</option>
          </Select>
          <Select label="Comprador" value={compradorId} onChange={v => { setCompradorId(v); setDados(null) }} disabled={loadingC || !leilaoId} className="flex-[2] min-w-[180px]">
            {compradores.length > 0 && <option value="todos">TODOS</option>}
            {compradores.map(c => <option key={c.id} value={String(c.id)}>{c.nome}</option>)}
          </Select>
          <div className="flex items-end gap-2 ml-auto">
            {loading && <RefreshCw size={15} className="text-gray-400 animate-spin mb-2" />}
            {/* Seletor versão */}
            {!loadingConfig && (
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 font-medium">Layout</label>
                <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm h-9">
                  {['1', '2'].map(v => (
                    <button key={v} type="button"
                      onClick={() => setVersao(v)}
                      className={`px-3 font-medium transition-colors ${versao === v ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                      V{v}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button onClick={compradorId === 'todos' ? handleImprimirGeral : handleImprimir} disabled={(compradorId !== 'todos' && !dados) || !compradorId || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors self-end">
              <Printer size={15} /> {loadingPdf ? 'Gerando...' : 'Imprimir'}
            </button>
            <button onClick={handleOrdemEmbarque} disabled={!leilaoId || loadingPdf}
              className="flex items-center gap-2 px-4 py-2 h-9 bg-red-600 hover:bg-red-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors self-end">
              <Truck size={15} /> Ordem
            </button>
          </div>
        </div>

        {/* Linha 2: Motorista + Placa + Frete */}
        <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-gray-100">
          <div className="flex flex-col gap-1 flex-1 min-w-[160px]" ref={autoRef}>
            <label className="text-xs text-gray-500 font-medium">Motorista</label>
            <div className="flex gap-1">
              <div className="relative flex-1">
                <input
                  type="text" value={motoristaNome}
                  onChange={e => handleMotoristaChange(e.target.value)}
                  onFocus={() => { if (sugestoes.length > 0) setDropdownAberto(true) }}
                  placeholder="Pesquisar ou digitar..." disabled={adicionado}
                  className="w-full border border-gray-300 rounded-lg pl-3 pr-8 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                  {buscandoMotorista
                    ? <RefreshCw size={13} className="text-blue-400 animate-spin" />
                    : motoristaNome ? <Search size={13} className="text-gray-300" /> : null}
                </div>
                {dropdownAberto && sugestoes.length > 0 && !adicionado && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-30 mt-1 max-h-48 overflow-y-auto">
                    {sugestoes.map((s, i) => (
                      <button key={i} type="button"
                        className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50 border-b border-gray-100 last:border-0"
                        onMouseDown={() => handleSelecionarSugestao(s)}>
                        <span className="font-medium text-gray-800">{s.motorista_nome}</span>
                        {s.placa && <span className="ml-2 font-mono text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">{s.placa}{s.placa_uf ? `/${s.placa_uf}` : ''}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {dropdownAberto && sugestoes.length === 0 && motoristaNome.trim() && !buscandoMotorista && !adicionado && (
                  <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-30 mt-1 px-3 py-2 text-xs text-gray-400">
                    Nenhum motorista encontrado — use o campo livremente
                  </div>
                )}
              </div>
              {!adicionado && (
                <button type="button" title="Cadastrar novo motorista" onClick={() => setShowMotoristaModal(true)}
                  className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-blue-600 transition-colors flex-shrink-0" style={{ height: 38 }}>
                  <Plus className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1 w-32">
            <label className="text-xs text-gray-500 font-medium">Placa</label>
            <input type="text" value={placa} onChange={e => handlePlacaChange(e.target.value)} placeholder="ABC-1234"
              maxLength={8} disabled={adicionado}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm uppercase focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500" />
          </div>

          <div className="flex flex-col gap-1 w-32">
            <label className="text-xs text-gray-500 font-medium">Frete (R$)</label>
            <input type="number" min="0" step="0.01" value={valorFrete}
              onChange={e => { setValorFrete(e.target.value); setAdicionado(false) }}
              placeholder="0,00" disabled={adicionado}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500" />
          </div>

          <div className="flex flex-col gap-1 w-20">
            <label className="text-xs text-gray-500 font-medium">Ordem</label>
            <input type="number" min="1" value={ordemEmbarque}
              onChange={e => { setOrdemEmbarque(e.target.value); setAdicionado(false) }}
              placeholder="—" disabled={adicionado}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-center font-bold focus:outline-none focus:ring-2 focus:ring-red-500 disabled:bg-gray-50 disabled:text-gray-500" />
          </div>

          {adicionado ? (
            <div className="flex items-center gap-2 self-end">
              <div className="flex items-center gap-1.5 text-green-600 text-sm font-medium bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <Check className="w-4 h-4" /> Adicionado
              </div>
              <button onClick={limparMotorista} title="Alterar" className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-gray-400 hover:text-red-500 transition-colors" style={{ height: 38 }}>
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button onClick={handleAdicionar}
              disabled={!podeAdicionar || salvando || !dados}
              className="flex items-center gap-2 px-4 py-2 h-9 self-end bg-green-600 hover:bg-green-700 disabled:bg-gray-200 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium transition-colors">
              {salvando ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              {salvando ? 'Salvando...' : 'Adicionar'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : !dados ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center text-gray-400 text-sm">{compradorId === 'todos' ? 'Clique em Imprimir para gerar a liberação de todos os compradores' : 'Selecione um comprador'}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
          {versao === '2' ? (
            <PreviewV2
              dados={dados}
              leilaoAtual={leilaoAtual}
              organizadorAtivo={organizadorAtivo}
              logoOrgSrc={logoOrgSrc}
              placa={placa}
              totalQtd={totalQtd}
              ordemEmbarque={ordemEmbarque}
              colunas={colunasV2}
            />
          ) : (
            <PreviewV1
              dados={dados}
              leilaoAtual={leilaoAtual}
              organizadorAtivo={organizadorAtivo}
              logoOrgSrc={logoOrgSrc}
              placa={placa}
              motoristaNome={motoristaNome}
              vFrete={vFrete}
              totalQtd={totalQtd}
              totalCriasFemea={totalCriasFemea}
              totalCriasMacho={totalCriasMacho}
              ordemEmbarque={ordemEmbarque}
              colunas={colunasV1}
            />
          )}
          </div>
        </div>
      )}

      {showPdf && pdfUrl && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl flex flex-col" style={{ height: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gray-50 rounded-t-xl flex-shrink-0">
              <span className="font-semibold text-gray-800">Liberação Curral — {dados?.comprador?.nome}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => downloadPdf(pdfUrl, `Liberação Curral - ${dados?.comprador?.nome || ''}.pdf`)}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-lg transition-colors">Salvar PDF</button>
                <button onClick={printPdf} className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"><Printer size={15} /> Imprimir</button>
                <button onClick={closePdf} className="px-3 py-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors text-sm">Fechar</button>
              </div>
            </div>
            <div className="flex-1 overflow-hidden rounded-b-xl"><iframe ref={iframeRef} src={pdfUrl} title="Liberação Curral" style={{ width: '100%', height: '100%', border: 'none' }} /></div>
          </div>
        </div>
      )}

      <MotoristaRapidoModal
        isOpen={showMotoristaModal}
        onClose={() => setShowMotoristaModal(false)}
        onSave={handleMotoristaCriado}
        organizadorId={orgId}
      />
    </div>
  )
}
